/*
 * Copyright (c) 2026. BrightNight. All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import { type FlowRunProgress, FlowRunState, type FlowRunStatus } from '@poweralpha/common';
import { fromNow } from '../../home/format';
import {
  formatPercent,
  formatRunCounts,
  formatRunTiming,
  isRunFailed,
  QUEUED_STATES,
} from './progress';
import type { ProgressTone } from './simProgressStyles';

export type RunGlyph = 'tasks' | 'cases' | 'queued' | 'done' | 'failed' | 'cancelled';

export interface RunRowDisplay {
  glyph: RunGlyph;
  tone: ProgressTone;
  /** Bar value from 0 to 1; null shows an indeterminate bar. */
  value: number | null;
  /** Right-aligned percentage or call to action. */
  action: string | null;
  meta: string;
  timing: string;
}

/** Display of a run that reached a terminal state, or null while it is still going. */
const getFinishedRowDisplay = (run: FlowRunStatus, canOpen: boolean): RunRowDisplay | null => {
  const timing = fromNow(run.end_time ?? run.created);
  if (run.state.type === FlowRunState.COMPLETED) {
    const action = canOpen ? 'Open' : null;
    return { glyph: 'done', tone: 'success', value: 1, action, meta: 'Completed', timing };
  }
  if (isRunFailed(run)) {
    const meta = run.state.type === FlowRunState.CRASHED ? 'Crashed' : 'Failed';
    return { glyph: 'failed', tone: 'error', value: 1, action: 'Details', meta, timing };
  }
  if (run.state.type === FlowRunState.CANCELLED) {
    return {
      glyph: 'cancelled',
      tone: 'neutral',
      value: 1,
      action: null,
      meta: 'Cancelled',
      timing,
    };
  }
  return null;
};

export const getRunRowDisplay = (
  run: FlowRunStatus,
  progress: FlowRunProgress | undefined,
  fraction: number | null,
  canOpen: boolean,
  now = Date.now(),
): RunRowDisplay => {
  if (QUEUED_STATES.includes(run.state.type)) {
    return {
      glyph: 'queued',
      tone: 'primary',
      value: null,
      action: null,
      meta: 'Queued · waiting for a worker',
      timing: 'Estimate after the first task',
    };
  }
  const finished = getFinishedRowDisplay(run, canOpen);
  if (finished) return finished;
  if (run.state.type !== FlowRunState.RUNNING) {
    // Cancelling or paused: keep the last known progress without an estimate.
    return {
      glyph: progress?.kind === 'cases' ? 'cases' : 'tasks',
      tone: 'neutral',
      value: fraction,
      action: null,
      meta: run.state.name ?? run.state.type.toLowerCase(),
      timing: formatRunTiming(run.start_time, null, now),
    };
  }
  return {
    glyph: progress?.kind === 'cases' ? 'cases' : 'tasks',
    tone: 'primary',
    value: fraction,
    action: fraction === null ? null : formatPercent(fraction),
    meta: formatRunCounts(progress) ?? 'Estimating…',
    timing: formatRunTiming(run.start_time, fraction, now),
  };
};
